import { Box, Stack } from '@mui/joy';
import {
  getPaginationRowModel,
  getSortedRowModel,
  type SortingOptions,
  type SortingTableState,
} from '@tanstack/react-table';
import type { CoreInstance, PaginationOptions, PaginationTableState } from '@tanstack/table-core';
import type { ReactElement } from 'react';
import type { GTableDynamicCursorBasedProps, GTableDynamicPageBasedProps, GTableProps } from './GTable.props';
import Loader from '../Loader/Loader.tsx';
import type { SxProps } from '@mui/system';
import { ErrorView } from '../errors/ErrorView.tsx';
export const isPageDynamic = <ROW,>(props: GTableProps<ROW>): props is GTableDynamicPageBasedProps<ROW> => {
  // biome-ignore lint/suspicious/noExplicitAny:
  return !!(props as any).pagePaginator;
};

export const isCursorDynamic = <ROW,>(props: GTableProps<ROW>): props is GTableDynamicCursorBasedProps<ROW> => {
  // biome-ignore lint/suspicious/noExplicitAny:
  return !!(props as any).cursorPaginator;
};

export const calculateSortingProps = <ROW,>(props: GTableProps<ROW>): SortingOptions<ROW> => {
  if (isPageDynamic(props) || isCursorDynamic(props)) {
    return {
      manualSorting: true,
      enableSorting: true,
      enableMultiSort: false,
      onSortingChange: props.onSortingChange,
      enableSortingRemoval: false,
    };
  }

  return {
    manualSorting: false,
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
    enableMultiSort: true,
  };
};

export const getBackgroundColor = (): string => {
  return 'none';
};

export const createTableElementProps = (): SxProps => ({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  tr: {
    border: 0,
    td: {
      padding: 0,
    },
  },
});

export const getBorderColor = (): string => {
  return 'var(--joy-palette-background-level2)';
};

export const calculateExtraRows = <ROW,>(props: GTableProps<ROW>, table: CoreInstance<ROW>): ReactElement[] => {
  const extraRows = [];
  if (isPageDynamic(props) || isCursorDynamic(props)) {
    if (props.loading) {
      extraRows.push(
        <tr key="loading">
          <td
            colSpan={table.getAllColumns().length}
            style={{
              borderBottomWidth: 0,
            }}
          >
            <Stack direction="row" justifyContent="center" spacing={1.5}>
              <Loader />
            </Stack>
          </td>
        </tr>
      );
    }

    if (props.error) {
      console.error('Error from api', props.error);
      extraRows.push(
        <tr key="error">
          <td
            colSpan={table.getAllColumns().length}
            style={{
              borderBottomWidth: 0,
              textAlign: 'center',
            }}
          >
            <ErrorView cause={props.error} variant="inline" />
          </td>
        </tr>
      );
    }
  }

  if (!props.hideNoDataMessage && extraRows.length === 0 && (props.data?.length ?? 0) === 0) {
    extraRows.push(
      <tr key="no-data">
        <Box
          component={'td'}
          colSpan={table.getAllColumns().length}
          sx={{ textAlign: 'center', paddingBlock: '0.75rem !important' }}
        >
          No data
        </Box>
      </tr>
    );
  }

  return extraRows;
};

export const calculatePaginationProps = (
  // biome-ignore lint/suspicious/noExplicitAny:
  props: GTableProps<any>
): PaginationOptions => {
  if (isPageDynamic(props)) {
    return {
      getPaginationRowModel: getPaginationRowModel(),
      manualPagination: true,
      pageCount: Math.ceil((props.totalResults ?? 0) / props.pagePaginator.state.pageSize),
      onPaginationChange: props.pagePaginator.onChange,
    };
  }

  if (isCursorDynamic(props)) {
    return {
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: props.cursorPaginator.onChange,
      manualPagination: true,
    };
  }
  if (props.disablePagination) {
    return {};
  }

  return {
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: false,
  };
};

export const calculateState = <ROW,>(props: GTableProps<ROW>): Partial<SortingTableState & PaginationTableState> => {
  if (isPageDynamic(props) || isCursorDynamic(props)) {
    const pagination = isPageDynamic(props) ? props.pagePaginator?.state : props.cursorPaginator?.state;
    const sorting = props.sortingState;
    return {
      sorting,
      pagination: {
        ...pagination,
      },
    };
  }

  return {};
};
